import React, { useEffect, useRef, useState } from 'react';
import { Search, X, UserPlus } from 'lucide-react';
import api from '../lib/api';

// Reusable searchable contact typeahead. Debounced server-side search against
// GET /contacts?search=…, AbortController to drop stale responses.

export interface PickedContact {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  idNumber?: string;
  city?: string;
  address?: string;
  supplierTaxId?: string;   // supplierProfile.taxId (ח.פ / עוסק / ת.ז)
}

interface Props {
  onSelect: (c: PickedContact | null) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /** Filter to a Contact.type, e.g. "supplier" (ספק). */
  type?: string;
  /** When provided, a "＋ add new" row appears in the dropdown. */
  onAddNew?: () => void;
  addNewLabel?: string;
}

export default function ContactPicker({ onSelect, placeholder = 'חפש איש קשר…', autoFocus, type, onAddNew, addNewLabel = 'הוסף איש קשר חדש' }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PickedContact[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (picked) return;
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      try {
        const params: Record<string, any> = { search: q, limit: 20, page: 1 };
        if (type) params.type = type;
        const res = await api.get('/contacts', { params, signal: ctrl.signal });
        const list = res.data?.contacts || res.data?.data || (Array.isArray(res.data) ? res.data : []);
        setResults(list.map((c: any) => ({
          _id: c._id,
          name: c.name || [c.firstName, c.lastName].filter(Boolean).join(' ') || '—',
          email: c.email,
          phone: c.phone,
          idNumber: c.idNumber,
          city: c.city,
          address: c.address,
          supplierTaxId: c.supplierProfile?.taxId,
        })));
        setOpen(true);
      } catch { /* aborted / error */ }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query, picked]);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const pick = (c: PickedContact) => { setPicked(true); setQuery(c.name); setOpen(false); onSelect(c); };
  const clear = () => { setPicked(false); setQuery(''); setResults([]); onSelect(null); };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Search size={15} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <input
          className="w-full pr-9 pl-8 py-2 border border-gray-300 dark:border-gray-600 dark:bg-[#2C2C2C] rounded-lg text-sm"
          placeholder={placeholder}
          value={query}
          autoFocus={autoFocus}
          onChange={(e) => { setQuery(e.target.value); if (picked) { setPicked(false); onSelect(null); } }}
          onFocus={() => { if (results.length || onAddNew) setOpen(true); }}
        />
        {(picked || query) && (
          <button type="button" onClick={clear} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500" aria-label="נקה">
            <X size={15} />
          </button>
        )}
      </div>
      {open && (results.length > 0 || loading || onAddNew) && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-auto bg-white dark:bg-[#1E1E1E] border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          {loading && <div className="px-3 py-2 text-xs text-gray-400">מחפש…</div>}
          {results.map((c) => (
            <button key={c._id} type="button" onClick={() => pick(c)}
              className="w-full text-right px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm">
              <div className="font-medium text-gray-900 dark:text-gray-100">{c.name}</div>
              {(c.phone || c.email) && <div className="text-xs text-gray-400">{[c.phone, c.email].filter(Boolean).join(' · ')}</div>}
            </button>
          ))}
          {!loading && results.length === 0 && !onAddNew && <div className="px-3 py-2 text-xs text-gray-400">לא נמצאו אנשי קשר</div>}
          {onAddNew && (
            <button type="button" onClick={() => { setOpen(false); onAddNew(); }}
              className="w-full flex items-center gap-2 text-right px-3 py-2 border-t border-gray-100 dark:border-gray-800 text-teal-700 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-950/30 text-sm font-medium">
              <UserPlus size={15} /> {addNewLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
